/**
 * FranchiseHierarchyPage — `/franchise/hierarchy`.
 *
 * Renders the parent → children tree using
 * `GET /v1/franchise/franchises` filtered by `parentId`. The user picks a
 * root franchise; its descendants are loaded via the franchise tree endpoint
 * `GET /v1/franchise/franchises/:id/tree`.
 */
import { type JSX, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getFranchiseTree,
  listFranchises,
  type Franchise,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

interface TreeNode {
  readonly franchise: Franchise;
  readonly children: readonly TreeNode[];
}

function buildTree(root: Franchise, descendants: readonly Franchise[]): TreeNode {
  const byParent = new Map<string | null, Franchise[]>();
  for (const d of descendants) {
    const list = byParent.get(d.parentId) ?? [];
    list.push(d);
    byParent.set(d.parentId, list);
  }
  const visit = (f: Franchise): TreeNode => ({
    franchise: f,
    children: (byParent.get(f.id) ?? []).map(visit),
  });
  return visit(root);
}

export function FranchiseHierarchyPage(): JSX.Element {
  const roots = useQuery({
    queryKey: ['franchise', 'hierarchy', 'roots'],
    queryFn: () => listFranchises({ pageSize: 50, tierCode: 'TIER_1' }),
    retry: false,
  });

  const [rootId, setRootId] = useState<string>('');
  const effectiveRootId =
    rootId.length > 0
      ? rootId
      : roots.data?.items[0]?.id ?? '';

  const tree = useQuery({
    queryKey: ['franchise', 'hierarchy', 'tree', effectiveRootId],
    queryFn: () => getFranchiseTree(effectiveRootId),
    retry: false,
    enabled: effectiveRootId.length > 0,
  });

  const denied =
    (roots.isError && roots.error instanceof ApiError &&
      (roots.error.status === 401 || roots.error.status === 403)) ||
    (tree.isError && tree.error instanceof ApiError &&
      (tree.error.status === 401 || tree.error.status === 403));

  const treeRoot = useMemo(() => {
    if (!tree.data) return null;
    return buildTree(tree.data.franchise, tree.data.descendants);
  }, [tree.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Franchise hierarchy"
        description="Parent and child franchises shown as a tree."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-muted">Root franchise</span>
        {roots.isPending ? (
          <p className="text-sm text-muted">Loading roots…</p>
        ) : roots.isError ? (
          <p className="text-sm text-danger">Roots unavailable.</p>
        ) : (
          <select
            value={effectiveRootId}
            onChange={(e) => setRootId(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">Select…</option>
            {roots.data?.items.map((r) => (
              <option key={r.id} value={r.id}>
                {r.legalName} ({r.franchiseNumber})
              </option>
            ))}
          </select>
        )}
      </div>

      {denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Franchise/read</code>.
        </DeferredNotice>
      ) : null}

      {effectiveRootId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Select a root franchise to render its hierarchy.
        </p>
      ) : tree.isPending ? (
        <p className="text-sm text-muted">Loading tree…</p>
      ) : tree.isError ? (
        <DeferredNotice title="Tree unavailable">{(tree.error as Error).message}</DeferredNotice>
      ) : treeRoot ? (
        <div className="bg-white border border-border rounded-xl p-4">
          <TreeNodeView node={treeRoot} depth={0} />
        </div>
      ) : null}
    </div>
  );
}

function TreeNodeView({ node, depth }: { node: TreeNode; depth: number }): JSX.Element {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;

  return (
    <div className="text-sm">
      <div
        className="flex items-center gap-2 py-1.5 hover:bg-gray-50 rounded px-2"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse' : 'Expand'}
            className="text-muted hover:text-gray-700"
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <span className="w-4 h-4 inline-block" />
        )}
        <Link
          to={`/franchise/franchises/${encodeURIComponent(node.franchise.id)}`}
          className="font-medium text-gray-900 hover:underline"
        >
          {node.franchise.legalName}
        </Link>
        <span className="text-[11px] font-mono text-muted">{node.franchise.franchiseNumber}</span>
        <span className="text-[11px] text-muted">{node.franchise.tierCode}</span>
        <StatusBadge status={node.franchise.status} />
      </div>
      {open && hasChildren ? (
        <div>
          {node.children.map((c) => (
            <TreeNodeView key={c.franchise.id} node={c} depth={depth + 1} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
